import React, { useLayoutEffect, useMemo, useState } from 'react';
import { GlobalOutlined } from '@ant-design/icons';
import { Dropdown, notification, Space } from 'antd';
import i18next, { t } from 'i18next';
import { validate as uuidValidate } from 'uuid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import QuickTripLogo from '../../assets/imgs/quick_trip_logo.svg?react';
import appStoreCn from '../../assets/imgs/AppleStore-cn.svg';
import appStoreEn from '../../assets/imgs/AppleStore-en.svg';
import appStoreJp from '../../assets/imgs/AppleStore-jp.svg';
import appStoreKo from '../../assets/imgs/AppleStore-ko.svg';
import appStoreTh from '../../assets/imgs/AppleStore-th.svg';
import appStoreVi from '../../assets/imgs/AppleStore-vi.svg';
import appStoreId from '../../assets/imgs/AppleStore-id.svg';
import googlePlayCn from '../../assets/imgs/GooglePlay-cn.png';
import googlePlayTw from '../../assets/imgs/GooglePlay-tw.png';
import googlePlayVi from '../../assets/imgs/GooglePlay-vi.png';
import googlePlayTh from '../../assets/imgs/GooglePlay-th.png';
import googlePlayKo from '../../assets/imgs/GooglePlay-ko.png';
import googlePlayEn from '../../assets/imgs/GooglePlay-en.png';
import googlePlayJp from '../../assets/imgs/GooglePlay-jp.png';
import googlePlayId from '../../assets/imgs/GooglePlay-id.png';
import { getSelectableLanguages } from '../../assets/languages/configs';
import CustomAntButton from '../../components/CustomAntButton/CustomAntButton';

import { applyFontFromLanguage, convertTextToLinks, showNotificationDialog } from '../../utils';
import {
    APP_URL_SCHEME,
    APPLE_STORE_URL,
    EXPIRATION_RECEIVE_TIME,
    GOOGLE_PLAY_URL,
    localStorageKeys,
} from '../../utils/Constants';

import styles from './styles.module.css';

const ReceiveLinkRelayScreen = () => {
    const [language, setLanguage] = useState(i18next.language);
    const [receiveId, setReceiveId] = useState<string>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const languages = useMemo(() => {
        const selectableLanguages = getSelectableLanguages();
        const languages = selectableLanguages.flatMap((item, index) => {
            if (selectableLanguages.length - 1 <= index) {
                return {
                    label: <div className={styles.language}>{item.title}</div>,
                    key: item.key,
                };
            } else {
                return [
                    {
                        label: <div className={styles.language}>{item.title}</div>,
                        key: item.key,
                    },
                    {
                        type: 'divider' as const,
                    },
                ];
            }
        });
        return languages;
    }, []);

    const { googlePlay, appleStore } = useMemo(() => {
        const appleStores: Record<string, string> = {
            vi: appStoreVi,
            ja: appStoreJp,
            en: appStoreEn,
            th: appStoreTh,
            ko: appStoreKo,
            zh: appStoreCn,
            'zh-TW': appStoreCn,
            id: appStoreId,
        };
        const googlePlays: Record<string, string> = {
            vi: googlePlayVi,
            ja: googlePlayJp,
            en: googlePlayEn,
            th: googlePlayTh,
            ko: googlePlayKo,
            zh: googlePlayCn,
            'zh-TW': googlePlayTw,
            id: googlePlayId,
        };
        return {
            appleStore: appleStores[language] || appStoreEn,
            googlePlay: googlePlays[language] || googlePlayEn,
        };
    }, [language]);

    useLayoutEffect(() => {
        if (!APP_URL_SCHEME) {
            navigate('/buy');
        }
        const receiveIdParam = searchParams.get('receiveId');
        if (receiveIdParam && uuidValidate(receiveIdParam)) {
            localStorage.setItem(
                localStorageKeys.RECEIVE_INFO,
                JSON.stringify({ receiveId: receiveIdParam, time: dayjs().toISOString() }),
            );
            setReceiveId(receiveIdParam);
        } else {
            const { receiveId, time } = JSON.parse(localStorage.getItem(localStorageKeys.RECEIVE_INFO) ?? '{}');

            if (receiveId && dayjs(time).add(EXPIRATION_RECEIVE_TIME, 'h').isAfter(dayjs())) {
                setReceiveId(receiveId);
            }
        }
    }, []);

    const onCopyText = async (text: string) => {
        try {
            await global.navigator.clipboard.writeText(text);
            notification.success({
                description: t('notification.copy-success'),
                message: '',
            });
        } catch (error) {
            console.error(error);
        }
    };

    /**
     * 言語変更の処理
     */
    const handleLangueChange = ({ key }: { key: string }) => {
        try {
            // 言語を切り替え
            setLanguage(key);
            void i18next.changeLanguage(key);
            applyFontFromLanguage(key);

            // ローカルストレージに言語値保存
            localStorage.setItem('lang', key);

            // 言語変更に完了のメッセージを表示
            showNotificationDialog(
                'success',
                'language-change',
                t('notification.title'),
                t('notification.language-change'),
            );
        } catch (error) {
            console.log(error);
        }
    };

    const handleOpenApp = () => {
        const appLink = `${APP_URL_SCHEME}?distribution-code=${receiveId}`;
        window.location.href = appLink;
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <QuickTripLogo className={styles.quickTripLogo} />
            </div>
            {receiveId ? (
                <div className={`content-container scrollable ${styles.container}`}>
                    <div className={styles.languages}>
                        <Dropdown
                            className={styles.dropdown}
                            menu={{
                                items: languages,
                                onClick: handleLangueChange,
                            }}
                            trigger={['click']}
                        >
                            <Space>
                                <GlobalOutlined />
                                {languages.find(({ key }) => key === language)?.label}
                            </Space>
                        </Dropdown>
                    </div>
                    <div className={styles.title}>{t('receive-link.title')}</div>
                    <div className={styles.appOpenContent}>
                        <div style={{ marginBottom: '10px' }} className={styles.descriptions}>
                            {convertTextToLinks(t('receive-link.actions-app-install'))}
                        </div>
                        <div className={styles.descriptions}>{convertTextToLinks(t('receive-link.step1'))}</div>
                        <div className={styles.actions}>
                            <a
                                href={APPLE_STORE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.actionBtn}
                            >
                                <img src={appleStore} alt="" />
                            </a>
                            <a
                                href={GOOGLE_PLAY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.actionBtn}
                            >
                                <img src={googlePlay} alt="" />
                            </a>
                        </div>
                    </div>
                    <div className={styles.appOpenContent}>
                        <div className={styles.descriptions}>{t('receive-link.app-open-title')}</div>
                        <CustomAntButton className={styles.button} block type="outlined" onClick={handleOpenApp}>
                            {t('button.receive-link-open-app')}
                        </CustomAntButton>
                    </div>
                    <div className={styles.divider}></div>
                    <div className={styles.appOpenContent}>
                        <div className={styles.descriptions}>{t('receive-link.link-copy-title1')}</div>
                        <div className={styles.title}>{t('receive-link.link-copy-title')}</div>
                        <div className={styles.label}>{t('receive-link.link-copy-label')}</div>
                        <div className={styles.link}>{receiveId}</div>
                        <CustomAntButton
                            onClick={() => onCopyText(receiveId)}
                            style={{ height: '40px' }}
                            className={styles.button}
                            type="outlined"
                        >
                            <CopyOutlined className={styles.copyIcon} />
                            {t('button.receive-link-copy-id')}
                        </CustomAntButton>
                    </div>
                </div>
            ) : (
                <div className={styles.noData}>{t('receive-link.no-data')}</div>
            )}
        </div>
    );
};

export default ReceiveLinkRelayScreen;
